import type { PostgrestError, SupabaseClient } from 'jsr:@supabase/supabase-js@2';

type DotNotationKeys<T, Prefix extends string = '', Depth extends any[] = []> = {
  [K in keyof T]: T[K] extends object
    ? Depth['length'] extends 5
      ? `${Prefix}${K & string}`
      : DotNotationKeys<T[K], `${Prefix}${K & string}.`, [...Depth, 0]> | `${Prefix}${K & string}`
    : `${Prefix}${K & string}`;
}[keyof T];

type Flatten<T> = {
  [K in keyof T]: T[K] extends (object & { length?: never }) | null ? string : T[K];
};

type Comparator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'is' | 'in' | 'or';
type AvailableFilterValues = string | number | boolean | null | undefined | string[] | number[] | boolean[];
export type Filters<T> = Partial<
  Record<
    DotNotationKeys<T>,
    | {
        comparator: Comparator;
        value: AvailableFilterValues;
      }
    | {
        comparator: Comparator;
        value: AvailableFilterValues;
      }[]
    | AvailableFilterValues
  >
>;
export type SelectOptions<T> = {
  order?: { by: keyof T; ascending: boolean };
  limit?: number;
  range?: [number, number];
};
type SingleResponse<T> = { data: T; error: null } | { data: null; error: PostgrestError };
type ManyResponse<T> = { data: T[]; error: null } | { data: null; error: PostgrestError };
export class SupabaseRepository<T extends { id: string | number; created_at?: string; updated_at?: string }> {
  constructor(
    private resource: string,
    private selectString: string,
    private supabaseClient: SupabaseClient
  ) {}

  async all(options: SelectOptions<T> | undefined = undefined): Promise<ManyResponse<T>> {
    const { data, error } = await this.selectOptionsQueryBuilder(
      this.supabaseClient.from(this.resource).select(this.selectString),
      options
    );

    return { data, error } as ManyResponse<T>;
  }

  async findOne(filters: Filters<T>): Promise<SingleResponse<T>> {
    const { data, error } = await this.selectQueryBuilder(filters).maybeSingle();

    return { data, error } as SingleResponse<T>;
  }

  async findMany(filters: Filters<T>, options: SelectOptions<T> | undefined = undefined): Promise<ManyResponse<T>> {
    const { data, error } = await this.selectOptionsQueryBuilder(this.selectQueryBuilder(filters), options);

    return { data, error } as ManyResponse<T>;
  }

  async create(model: Partial<Omit<Flatten<T>, 'id' | 'created_at' | 'updated_at'>>): Promise<SingleResponse<T>> {
    const { data, error } = await this.supabaseClient
      .from(this.resource)
      .insert(model)
      .select(this.selectString)
      .maybeSingle();

    return { data, error } as SingleResponse<T>;
  }

  async createMany(models: Partial<Omit<Flatten<T>, 'id' | 'created_at' | 'updated_at'>>[]): Promise<ManyResponse<T>> {
    const { data, error } = await this.supabaseClient.from(this.resource).insert(models).select(this.selectString);

    return { data, error } as ManyResponse<T>;
  }

  async update(
    model: Partial<Omit<Flatten<T>, 'id' | 'created_at' | 'updated_at'>>,
    filters: Filters<T>
  ): Promise<SingleResponse<T>> {
    const { data, error } = await this.queryBuilder(this.supabaseClient.from(this.resource).update(model), filters)
      .select(this.selectString)
      .maybeSingle();

    return { data, error } as SingleResponse<T>;
  }

  async upsert(
    models: Partial<Omit<Flatten<T>, 'id' | 'created_at' | 'updated_at'>>[],
    options?: { onConflict?: keyof T; ignoreDuplicates?: boolean; defaultToNull?: boolean }
  ): Promise<ManyResponse<T>> {
    const { data, error } = await this.supabaseClient
      .from(this.resource)
      .upsert(models, options ? (options as { onConflict?: string }) : undefined)
      .select(this.selectString);

    return { data, error } as ManyResponse<T>;
  }

  async delete(filters: Filters<T>): Promise<{ error: PostgrestError | null }> {
    const { error } = await this.queryBuilder(this.supabaseClient.from(this.resource).delete(), filters);

    return { error } as { error: PostgrestError | null };
  }

  private queryBuilder(query: any, filters: Filters<T>) {
    let localQuery = query;
    Object.entries(filters).forEach(([key, param]) => {
      if (!param) return;

      // @ts-expect-error generic type
      if (param.comparator) {
        // @ts-expect-error generic type
        localQuery = localQuery[param.comparator](key, param.value);
      } else if (Array.isArray(param)) {
        localQuery = param.reduce((acc, current) => {
          acc[current.comparator](key, current.value);
          return acc;
        }, localQuery);
      } else {
        localQuery = localQuery.eq(key, param);
      }
    });

    return localQuery;
  }

  private selectOptionsQueryBuilder(query: any, options?: SelectOptions<T>) {
    if (!options) {
      return query;
    }
    let localQuery = query;
    Object.entries(options).forEach(([key, param]) => {
      switch (key) {
        case 'order': {
          const option = param as NonNullable<SelectOptions<T>['order']>;
          localQuery = localQuery.order(option.by, { ascending: option.ascending });
          break;
        }
        case 'limit': {
          const option = param as NonNullable<SelectOptions<T>['limit']>;
          localQuery = localQuery.limit(option);
          break;
        }
        case 'range': {
          const option = param as NonNullable<SelectOptions<T>['range']>;
          localQuery = localQuery.range(option[0], option[1]);
          break;
        }
        default:
          break;
      }
    });

    return localQuery;
  }

  private selectQueryBuilder(filters: Filters<T>) {
    return this.queryBuilder(this.supabaseClient.from(this.resource).select(this.selectString), filters);
  }
}
